import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { AddressRecord } from '@navio/nsdk';
import type { BookingAddress } from 'noddi-types';
import { cn, NoddiIcon } from 'noddi-ui-common';
import { getLongAddressName } from 'noddi-web';
import { formatServiceCenterName } from '../../utils/formatServiceCenterName';

type AddressInput =
  | AddressRecord
  | BookingAddress
  | {
      displayText: string;
      googleMapsUrl: string;
    };

type Props = {
  /** Site / garage display name. */
  name: string;
  address: AddressInput;
  /** Optional brand label; skipped when `name` already starts with it. */
  brandLabel?: string;
  /** Small "Service center" title above the name. */
  showLabel?: boolean;
  className?: string;
};

/**
 * Service center name + address with a Google Maps open icon.
 * Shared by booking details, stationary summary, and proposal checkout.
 */
export const ServiceCenterLocation = ({ name, address, brandLabel, showLabel = false, className }: Props) => {
  const displayName = formatServiceCenterName(brandLabel, name);
  const displayText = 'displayText' in address ? address.displayText : getLongAddressName(address);
  const googleMapsUrl = address.googleMapsUrl;

  if (!displayName && !displayText) {
    return null;
  }

  return (
    <div className={cn('flex gap-2', className)}>
      <NoddiIcon name='MapPin' className='mt-0.5 shrink-0' />
      <div className='flex min-w-0 flex-col'>
        {showLabel && (
          <span className='text-sm text-muted-foreground'>
            <Trans>Service center</Trans>
          </span>
        )}
        {displayName && <span className={cn(showLabel && 'font-semibold')}>{displayName}</span>}
        {displayText && (
          <div className='flex items-center gap-2'>
            <span className={showLabel || displayName ? 'text-sm text-muted-foreground' : undefined}>
              {displayText}
            </span>
            <a
              href={googleMapsUrl}
              target='_blank'
              rel='noopener noreferrer'
              aria-label={t`Open in Google Maps (opens in a new tab)`}
              className='shrink-0 text-muted-foreground hover:text-primary'
            >
              <NoddiIcon name='Map' size='small' />
            </a>
          </div>
        )}
      </div>
    </div>
  );
};
